import type { ActionType, InputMapping } from "../types/action";
import type { DebugSnapshot } from "../types/debug";
import type { GridPoint } from "../types/grid";
import type { LevelData } from "../types/level";
import { createLevelBounds, getTileAt, hasPlankAt, isPointWithinBounds } from "./Grid";
import { computeFog } from "./FogSystem";
import type { LevelState, LevelUndoState } from "./LevelState";
import { createUndoFrame, restorePreviousState } from "./UndoSystem";

export interface RuntimeActionRequest {
  readonly rawInput: string;
  readonly mappedInput: InputMapping["mapped"];
}

export interface PlaybackMetadata {
  readonly kind: string;
  readonly from: GridPoint;
  readonly contact: GridPoint;
  readonly settled: GridPoint;
  readonly blocksInput: boolean;
  readonly durationMs: number;
  readonly flashPlayer: boolean;
  readonly fallPath?: readonly GridPoint[];
}

export interface SettlementResult {
  readonly contact: GridPoint;
  readonly settled: GridPoint;
  readonly fallPath: readonly GridPoint[];
}

export interface ActionResolution {
  readonly state: LevelState;
  readonly snapshot: DebugSnapshot;
  readonly valid: boolean;
  readonly reason: string | null;
  readonly playback?: Readonly<PlaybackMetadata>;
}

const INPUT_MAPPING_BY_RAW: Readonly<Record<string, InputMapping["mapped"]>> = {
  left: "left",
  ArrowLeft: "left",
  a: "left",
  A: "left",
  KeyA: "left",
  right: "right",
  ArrowRight: "right",
  d: "right",
  D: "right",
  KeyD: "right",
  up: "up",
  ArrowUp: "up",
  w: "up",
  W: "up",
  KeyW: "up",
  down: "down",
  ArrowDown: "down",
  s: "down",
  S: "down",
  KeyS: "down",
  jump: "jump",
  Space: "jump",
  Spacebar: "jump",
  " ": "jump",
  undo: "undo",
  z: "undo",
  Z: "undo",
  KeyZ: "undo",
  Tab: "openNotes",
  r: "restart",
  R: "restart",
  KeyR: "restart",
  f: "toggleDebugFog",
  F: "toggleDebugFog",
  KeyF: "toggleDebugFog",
  Escape: "returnToLevelSelect",
  Esc: "returnToLevelSelect",
};

function cloneStateForSnapshot(state: LevelState): DebugSnapshot["state"] {
  return {
    player: {
      x: state.player.x,
      y: state.player.y,
      facing: state.player.facing,
    },
    history: {
      length: state.history.length,
      last5: [...state.history.last5],
    },
    fog: {
      explored: state.fog.explored.map((point) => ({ x: point.x, y: point.y })),
      permanentlyRevealed: state.fog.permanentReveal,
      debugReveal: state.fog.debugReveal,
    },
    collectibles: {
      local: [...state.collectibles.local],
      permanent: [...state.collectibles.permanent],
    },
    breadcrumbs: {
      active: state.breadcrumbs.active.map((b) => ({ ...b })),
      local: [...state.breadcrumbs.local],
    },
    symbolSequence: {
      raw: [...state.symbolSequence.raw],
      expanded: [...state.symbolSequence.expanded],
      playerNotes: { ...state.symbolSequence.playerNotes },
    },
  };
}

function createDebugSnapshot(
  input: InputMapping,
  state: LevelState,
  valid: boolean,
  reason: string | null,
  resultSubtype?: string,
  resolutionKind?: string,
): DebugSnapshot {
  return {
    tick: state.tick,
    input,
    result: valid ? "valid" : "invalid",
    reason,
    ...(resultSubtype !== undefined ? { resultSubtype } : {}),
    ...(resolutionKind !== undefined ? { resolutionKind } : {}),
    state: cloneStateForSnapshot(state),
  };
}

function finalizeResolution(
  input: InputMapping,
  state: LevelState,
  valid: boolean,
  reason: string | null,
  playback?: Readonly<PlaybackMetadata>,
  resultSubtype?: string,
  resolutionKind?: string,
): ActionResolution {
  const snapshot = createDebugSnapshot(input, state, valid, reason, resultSubtype, resolutionKind);
  const finalizedState: LevelState = {
    ...state,
    lastDebugSnapshot: snapshot,
  };

  return {
    state: finalizedState,
    snapshot,
    valid,
    reason,
    ...(playback !== undefined ? { playback } : {}),
  };
}

function isSolidTile(level: LevelData, point: GridPoint): boolean {
  return getTileAt(level.grid, point) === "solid";
}

function isLadderTile(level: LevelData, point: GridPoint): boolean {
  return getTileAt(level.grid, point) === "ladder";
}

function canOccupy(level: LevelData, point: GridPoint): boolean {
  return isPointWithinBounds(point, createLevelBounds(level.grid)) && !isSolidTile(level, point);
}

function hasSolidSupport(level: LevelData, point: GridPoint): boolean {
  return isSolidTile(level, { x: point.x, y: point.y + 1 });
}

function isStableJumpLanding(level: LevelData, point: GridPoint): boolean {
  return hasPlankAt(level.grid, point) || hasSolidSupport(level, point);
}

function isStablePosition(level: LevelData, point: GridPoint): boolean {
  return isLadderTile(level, point) || hasPlankAt(level.grid, point) || hasSolidSupport(level, point);
}

function settlePortalExit(level: LevelData, portalDest: GridPoint): { settled: GridPoint; fallPath: GridPoint[] } {
  let current = portalDest;
  const fallPath: GridPoint[] = [];

  while (!isStablePosition(level, current)) {
    const next: GridPoint = { x: current.x, y: current.y + 1 };
    if (!canOccupy(level, next)) {
      break;
    }
    current = next;
    fallPath.push(current);
  }

  return { settled: current, fallPath };
}

function settleAfterHorizontalMove(level: LevelData, point: GridPoint): SettlementResult | null {
  if (!canOccupy(level, point)) {
    return null;
  }

  let current = point;
  const fallPath: GridPoint[] = [];

  while (!isStablePosition(level, current)) {
    const next = { x: current.x, y: current.y + 1 };
    if (!canOccupy(level, next)) {
      return null;
    }

    current = next;
    fallPath.push(current);
  }

  return { contact: point, settled: current, fallPath };
}

function canClimbVertically(level: LevelData, from: GridPoint, to: GridPoint): boolean {
  if (!isLadderTile(level, from)) {
    return false;
  }

  if (!canOccupy(level, to)) {
    return false;
  }

  return isStablePosition(level, to);
}

function appendHistory(state: LevelState, action: ActionType): LevelState["history"] {
  return {
    length: state.history.length + 1,
    last5: [...state.history.last5, action].slice(-5),
  };
}

function allLevelCollectiblesCollected(level: LevelData, state: LevelState): boolean {
  const collectedIds = new Set(state.collectibles.local);
  return level.collectibles.every((collectible) => collectedIds.has(collectible.id));
}

function resolvePortalDestination(level: LevelData, point: GridPoint): GridPoint {
  const portal = level.portals.find((entry) => entry.x === point.x && entry.y === point.y);
  if (portal === undefined) {
    return point;
  }

  const destination = level.portals.find((entry) => entry.group === portal.group && entry.id !== portal.id);
  if (destination === undefined) {
    return point;
  }

  return {
    x: destination.x,
    y: destination.y,
  };
}

function applyCollectibleSettlement(level: LevelData, state: LevelState, point: GridPoint): LevelState {
  const collectible = level.collectibles.find((entry) => entry.x === point.x && entry.y === point.y);
  if (collectible === undefined) {
    return state;
  }

  if (state.collectibles.local.includes(collectible.id)) {
    return state;
  }

  return {
    ...state,
    collectibles: {
      ...state.collectibles,
      local: [...state.collectibles.local, collectible.id],
    },
  };
}

function applyCollectiblesAlongPath(level: LevelData, state: LevelState, contact: GridPoint, fallPath: readonly GridPoint[]): LevelState {
  let current = applyCollectibleSettlement(level, state, contact);
  for (const point of fallPath) {
    current = applyCollectibleSettlement(level, current, point);
  }
  return current;
}

function applyBreadcrumbSettlement(level: LevelData, state: LevelState, point: GridPoint): LevelState {
  const breadcrumb = state.breadcrumbs.active.find((entry) => entry.x === point.x && entry.y === point.y);
  if (breadcrumb === undefined) return state;
  if (state.breadcrumbs.local.includes(breadcrumb.id)) return state;
  return {
    ...state,
    breadcrumbs: {
      ...state.breadcrumbs,
      local: [...state.breadcrumbs.local, breadcrumb.id],
    },
  };
}

function applyBreadcrumbsAlongPath(level: LevelData, state: LevelState, contact: GridPoint, fallPath: readonly GridPoint[]): LevelState {
  let current = applyBreadcrumbSettlement(level, state, contact);
  for (const point of fallPath) {
    current = applyBreadcrumbSettlement(level, current, point);
  }
  return current;
}

function applyExitSettlement(level: LevelData, state: LevelState, point: GridPoint): LevelState {
  if (point.x !== level.exit.x || point.y !== level.exit.y) {
    return state;
  }

  return {
    ...state,
    completion: {
      cleared: true,
      perfected: allLevelCollectiblesCollected(level, state),
    },
  };
}

function updateFog(state: LevelState, level: LevelData): LevelState {
  return {
    ...state,
    fog: computeFog(state, level),
  };
}

function restoreUndoState(
  state: LevelState,
  restored: LevelUndoState,
  remainingFrames: ReadonlyArray<ReturnType<typeof createUndoFrame>>,
): LevelState {
  return {
    ...state,
    player: restored.player,
    history: restored.history,
    fog: restored.fog,
    collectibles: restored.collectibles,
    breadcrumbs: restored.breadcrumbs,
    completion: restored.completion,
    undoStack: remainingFrames,
  };
}

function resolveMovement(action: Exclude<ActionType, "jump">, state: LevelState, level: LevelData): SettlementResult | null {
  const current = state.player;

  switch (action) {
    case "left":
      return settleAfterHorizontalMove(level, { x: current.x - 1, y: current.y });
    case "right":
      return settleAfterHorizontalMove(level, { x: current.x + 1, y: current.y });
    case "up": {
      const target = { x: current.x, y: current.y - 1 };
      return canClimbVertically(level, current, target)
        ? { contact: target, settled: target, fallPath: [] }
        : null;
    }
    case "down": {
      const target = { x: current.x, y: current.y + 1 };
      return canClimbVertically(level, current, target)
        ? { contact: target, settled: target, fallPath: [] }
        : null;
    }
    case "undo":
      return null;
  }
}

function createInvalidReason(action: Exclude<ActionType, "jump">, state: LevelState, level: LevelData): string {
  const current = state.player;

  switch (action) {
    case "left":
    case "right": {
      const target = {
        x: current.x + (action === "left" ? -1 : 1),
        y: current.y,
      };

      if (!isPointWithinBounds(target, createLevelBounds(level.grid))) {
        return "Movement would leave level bounds.";
      }

      if (isSolidTile(level, target)) {
        return "Movement blocked by solid tile.";
      }

      return "Movement cannot settle onto a supported tile.";
    }
    case "up":
    case "down":
      return "Cannot move vertically when not on a ladder.";
    case "undo":
      return "Cannot undo at the initial state.";
  }
}

export function createInputMapping(raw: string): InputMapping {
  return {
    raw,
    mapped: INPUT_MAPPING_BY_RAW[raw] ?? null,
  };
}

function createInvalidFlashPlayback(player: GridPoint): PlaybackMetadata {
  return {
    kind: "invalid-flash",
    from: { x: player.x, y: player.y },
    contact: { x: player.x, y: player.y },
    settled: { x: player.x, y: player.y },
    blocksInput: true,
    durationMs: 200,
    flashPlayer: true,
  };
}

function resolveJump(
  input: InputMapping,
  levelState: LevelState,
  level: LevelData,
  baseState: LevelState,
): ActionResolution {
  const origin = { x: levelState.player.x, y: levelState.player.y };
  const target = { x: origin.x, y: origin.y - 1 };

  if (!canOccupy(level, target)) {
    const playback: PlaybackMetadata = {
      ...createInvalidFlashPlayback(levelState.player),
      contact: target,
    };
    return finalizeResolution(input, baseState, false, "Jump target is blocked.", playback);
  }

  if (!hasSolidSupport(level, origin) && !hasPlankAt(level.grid, origin)) {
    return finalizeResolution(
      input,
      baseState,
      false,
      "Jump requires solid ground or plank.",
      createInvalidFlashPlayback(levelState.player),
    );
  }

  const undoFrame = createUndoFrame(levelState, "jump");
  const nextFacing = levelState.player.facing;

  if (isStableJumpLanding(level, target)) {
    const portalRaw = resolvePortalDestination(level, target);
    const isTeleport = portalRaw.x !== target.x || portalRaw.y !== target.y;

    let settledPoint: GridPoint;
    let portalFallPath: readonly GridPoint[];

    if (isTeleport) {
      const portalSettle = settlePortalExit(level, portalRaw);
      if (portalSettle.fallPath.length > 0) {
        portalFallPath = [portalRaw, ...portalSettle.fallPath];
        settledPoint = portalSettle.settled;
      } else {
        portalFallPath = [];
        settledPoint = portalRaw;
      }
    } else {
      portalFallPath = [];
      settledPoint = portalRaw;
    }

    let nextState: LevelState = {
      ...baseState,
      player: {
        x: settledPoint.x,
        y: settledPoint.y,
        facing: nextFacing,
      },
      history: appendHistory(levelState, "jump"),
      undoStack: [...levelState.undoStack, undoFrame],
    };

    if (isTeleport && portalFallPath.length > 0) {
      nextState = applyCollectiblesAlongPath(level, nextState, portalRaw, portalFallPath);
      nextState = applyBreadcrumbsAlongPath(level, nextState, portalRaw, portalFallPath);
    }
    nextState = applyCollectibleSettlement(level, nextState, settledPoint);
    nextState = applyBreadcrumbSettlement(level, nextState, settledPoint);
    nextState = applyExitSettlement(level, nextState, settledPoint);
    nextState = updateFog(nextState, level);

    if (portalFallPath.length > 0) {
      const playback: PlaybackMetadata = {
        kind: "walk-settle",
        from: origin,
        contact: target,
        settled: settledPoint,
        blocksInput: true,
        durationMs: 80 + portalFallPath.length * 60,
        flashPlayer: false,
        fallPath: portalFallPath,
      };
      return finalizeResolution(input, nextState, true, null, playback);
    }

    const playback: PlaybackMetadata = {
      kind: "jump-land",
      from: origin,
      contact: target,
      settled: settledPoint,
      blocksInput: false,
      durationMs: 0,
      flashPlayer: false,
    };

    return finalizeResolution(input, nextState, true, null, playback);
  }

  const portalRaw = resolvePortalDestination(level, target);
  const isTeleport = portalRaw.x !== target.x || portalRaw.y !== target.y;

  if (isTeleport) {
    const portalSettle = settlePortalExit(level, portalRaw);
    let settledPoint: GridPoint;
    let portalFallPath: readonly GridPoint[];

    if (portalSettle.fallPath.length > 0) {
      portalFallPath = [portalRaw, ...portalSettle.fallPath];
      settledPoint = portalSettle.settled;
    } else {
      portalFallPath = [];
      settledPoint = portalRaw;
    }

    let nextState: LevelState = {
      ...baseState,
      player: {
        x: settledPoint.x,
        y: settledPoint.y,
        facing: nextFacing,
      },
      history: appendHistory(levelState, "jump"),
      undoStack: [...levelState.undoStack, undoFrame],
    };

    nextState = applyCollectibleSettlement(level, nextState, target);
    nextState = applyBreadcrumbSettlement(level, nextState, target);
    nextState = applyCollectiblesAlongPath(level, nextState, portalRaw, portalFallPath);
    nextState = applyBreadcrumbsAlongPath(level, nextState, portalRaw, portalFallPath);
    nextState = applyCollectibleSettlement(level, nextState, settledPoint);
    nextState = applyBreadcrumbSettlement(level, nextState, settledPoint);
    nextState = applyExitSettlement(level, nextState, settledPoint);
    nextState = updateFog(nextState, level);

    if (portalFallPath.length > 0) {
      const playback: PlaybackMetadata = {
        kind: "walk-settle",
        from: origin,
        contact: target,
        settled: settledPoint,
        blocksInput: true,
        durationMs: 80 + portalFallPath.length * 60,
        flashPlayer: false,
        fallPath: portalFallPath,
      };
      return finalizeResolution(input, nextState, true, null, playback);
    }

    const playback: PlaybackMetadata = {
      kind: "jump-land",
      from: origin,
      contact: target,
      settled: settledPoint,
      blocksInput: false,
      durationMs: 0,
      flashPlayer: false,
    };
    return finalizeResolution(input, nextState, true, null, playback);
  }

  const settledPoint = origin;

  let nextState: LevelState = {
    ...baseState,
    player: {
      x: settledPoint.x,
      y: settledPoint.y,
      facing: nextFacing,
    },
    history: appendHistory(levelState, "jump"),
    undoStack: [...levelState.undoStack, undoFrame],
  };

  nextState = applyCollectibleSettlement(level, nextState, target);
  nextState = applyBreadcrumbSettlement(level, nextState, target);
  nextState = applyExitSettlement(level, nextState, settledPoint);
  nextState = updateFog(nextState, level);

  const playback: PlaybackMetadata = {
    kind: "jump-reach-return",
    from: origin,
    contact: target,
    settled: settledPoint,
    blocksInput: true,
    durationMs: 200,
    flashPlayer: false,
  };

  return finalizeResolution(
    input,
    nextState,
    true,
    null,
    playback,
    "reach-return",
    "jump-reach-return",
  );
}

export function resolveAction(
  request: RuntimeActionRequest,
  levelState: LevelState,
  level: LevelData,
): ActionResolution {
  const input: InputMapping = {
    raw: request.rawInput,
    mapped: request.mappedInput,
  };

  const baseState: LevelState = {
    ...levelState,
    tick: levelState.tick + 1,
    lastDebugSnapshot: null,
  };

  const action = request.mappedInput;
  if (action === null || action === "restart" || action === "toggleDebugFog" || action === "openNotes" || action === "returnToLevelSelect") {
    return finalizeResolution(input, baseState, false, "Input does not map to a runtime action.");
  }

  if (action === "undo") {
    const restored = restorePreviousState(levelState.undoStack);
    if (!restored.valid || restored.state === null) {
      return finalizeResolution(input, baseState, false, restored.reason, createInvalidFlashPlayback(levelState.player));
    }

    const restoredState = restoreUndoState(baseState, restored.state, restored.remainingFrames);
    return finalizeResolution(input, restoredState, true, null);
  }

  if (action === "jump") {
    return resolveJump(input, levelState, level, baseState);
  }

  const result = resolveMovement(action, levelState, level);
  if (result === null) {
    return finalizeResolution(
      input,
      baseState,
      false,
      createInvalidReason(action, levelState, level),
      createInvalidFlashPlayback(levelState.player),
    );
  }

  const undoFrame = createUndoFrame(levelState, action);
  const nextFacing = action === "left" || action === "right"
    ? action
    : levelState.player.facing;

  const portalRaw = resolvePortalDestination(level, result.settled);
  const isTeleport = portalRaw.x !== result.settled.x || portalRaw.y !== result.settled.y;

  let settledPoint: GridPoint;
  let combinedFallPath: readonly GridPoint[];

  if (isTeleport) {
    const portalSettle = settlePortalExit(level, portalRaw);
    if (portalSettle.fallPath.length > 0) {
      combinedFallPath = [...result.fallPath, portalRaw, ...portalSettle.fallPath];
      settledPoint = portalSettle.settled;
    } else {
      combinedFallPath = result.fallPath;
      settledPoint = portalRaw;
    }
  } else {
    combinedFallPath = result.fallPath;
    settledPoint = portalRaw;
  }

  let nextState: LevelState = {
    ...baseState,
    player: {
      x: settledPoint.x,
      y: settledPoint.y,
      facing: nextFacing,
    },
    history: appendHistory(levelState, action),
    undoStack: [...levelState.undoStack, undoFrame],
  };

  nextState = applyCollectiblesAlongPath(level, nextState, result.contact, combinedFallPath);
  nextState = applyBreadcrumbsAlongPath(level, nextState, result.contact, combinedFallPath);
  nextState = applyCollectibleSettlement(level, nextState, settledPoint);
  nextState = applyBreadcrumbSettlement(level, nextState, settledPoint);
  nextState = applyExitSettlement(level, nextState, settledPoint);
  nextState = updateFog(nextState, level);

  if (combinedFallPath.length > 0) {
    const playback: PlaybackMetadata = {
      kind: "walk-settle",
      from: { x: levelState.player.x, y: levelState.player.y },
      contact: result.contact,
      settled: settledPoint,
      blocksInput: true,
      durationMs: 80 + combinedFallPath.length * 60,
      flashPlayer: false,
      fallPath: combinedFallPath,
    };
    return finalizeResolution(input, nextState, true, null, playback);
  }

  return finalizeResolution(input, nextState, true, null);
}
